import * as React from "react";

import { useToggleButton_unstable } from "@fluentui/react-components";

import { useMergedTooltipAndKeytipRef } from "@1js/acui-hooks";
import type { AppMergedTooltipAndKeytipProps } from "@1js/acui-hooks";

import { deriveButtonKeytip } from "@1js/acui-keytip";
import { useAppButtonState } from "../../AppButton/next";
import type {
  AppToggleButtonProps,
  AppToggleButtonState,
} from "./AppToggleButton.types";
import { AppContext } from "@1js/acui-context";
import { checkExperiment, ribbonExperiments } from "@1js/acui-experiments";

/**
 * Given user props, returns state for an `AppToggleButton`.
 */
export const useAppToggleButton = (
  props: AppToggleButtonProps,
  ref: React.Ref<HTMLButtonElement>
): AppToggleButtonState => {
  const { disabled, toggled, toggleUsingIcon } = props;

  const mergedRefWithKeytip = useMergedTooltipAndKeytipRef(
    {
      ...props,
      deriveKeytip: deriveButtonKeytip,
    } as AppMergedTooltipAndKeytipProps,
    ref,
    "AppButton"
  );

  const toggleButtonState = useToggleButton_unstable(
    { ...props, checked: toggled, type: "button", disabledFocusable: disabled },
    mergedRefWithKeytip
  );

  const context = React.useContext(AppContext);
  const hideIconUsingToggleEnabled = !!checkExperiment(
    context,
    ribbonExperiments.fluentV9ToggleIconEnabled
  );

  const hideIcon = hideIconUsingToggleEnabled && toggleUsingIcon && !toggled;

  useAppButtonState({ ...props, hideIcon }, toggleButtonState);

  return toggleButtonState;
};
